import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { loadModelPack } from '../render/models/modelLibrary';
import { tyreGeometry } from './meshes';
import { kitPartsArrived, registerKitPart, toKitGeometry } from './packGeometry';

/**
 * The Blender wheels (docs/ASSETS.md "Blender rims"): Pike's six premium rims (shared/garage.ts RIMS) in
 * public/models/rims-pack.glb and the eight period wheels (meshes.ts WheelStyle: hubcap, chrome, wire, steel, disc,
 * truck, minilite, rostyle) in public/models/wheels-pack.glb (art/models/blender/rims_gen.py, wheels_gen.py →
 * scripts/build-rims.mjs): one indexed geometry per wheel and LOD (high / mid / low for the HIGH / MEDIUM / LOW
 * tiers) with the wheel shader's `surf` in `_SURF`, the axle already along +X. A premium rim is merged with the
 * game's own tyre (meshes.ts tyreGeometry); a period wheel brings its tyre with it. Each pack is fetched the first
 * time a car wants a wheel from it (wheelBatch.ts) or when Pike's bay opens — the rims pack never in a scene where
 * nobody drives them — and until it is in (or where it never comes: tests, a failed fetch) the wheel draws
 * meshes.ts's procedural one. The wheels pack also carries the car-kit parts (the police lamp): packGeometry.ts.
 */
export const BLENDER_RIMS: ReadonlySet<string> = new Set(['lattice', 'forged', 'ninestar', 'twelve', 'twinspoke', 'honeycomb']);
export const BLENDER_WHEELS: ReadonlySet<string> = new Set(['hubcap', 'chrome', 'wire', 'steel', 'disc', 'truck', 'minilite', 'rostyle']);
/** Bead-seat radius of the six (a modern low-profile look; the period wheels sit at 0.56–0.68). */
export const BLENDER_RIM_RADIUS = 0.7;

export type RimLod = 'high' | 'mid' | 'low';
const LODS: readonly RimLod[] = ['high', 'mid', 'low'];

/** The LOD a detail tier draws (vehicleAmbient.tier: 0 LOW, 1 MEDIUM, 2 HIGH). */
export function rimLodFor(tier: number): RimLod {
  return tier >= 2 ? 'high' : tier === 1 ? 'mid' : 'low';
}

/** Whether a wheel style has a Blender model (in one of the two packs). */
export function isBlenderWheel(style: string): boolean {
  return BLENDER_RIMS.has(style) || BLENDER_WHEELS.has(style);
}

const ready = new Map<string, THREE.BufferGeometry>();
const listeners: ((style: string) => void)[] = [];
const requested = new Set<string>();

/** A wheel's geometry (tyre included) once its pack is in, else null. */
export function rimGeometry(style: string, lod: RimLod): THREE.BufferGeometry | null {
  return ready.get(`${style}:${lod}`) ?? null;
}

/** Hear of every wheel whose geometry has arrived (wheelBatch swaps the batch's geometry). */
export function onRimModels(cb: (style: string) => void): void {
  listeners.push(cb);
}

function request(pack: 'rims' | 'wheels'): void {
  if (requested.has(pack)) return;
  requested.add(pack);
  const ids = pack === 'rims' ? BLENDER_RIMS : BLENDER_WHEELS;
  void loadModelPack(pack).then((p) => {
    if (!p) return;
    for (const id of ids) {
      for (const lod of LODS) {
        const m = p.get(id, lod);
        if (m && m.lod === lod) ready.set(`${id}:${lod}`, assembleRim(m.geometry, lod, pack === 'rims'));
      }
      for (const cb of listeners) cb(id);
    }
    if (pack === 'wheels') {
      for (const id of p.ids()) {
        if (ids.has(id)) continue;
        for (const lod of ['high', 'mid']) {
          const m = p.get(id, lod);
          if (m && m.lod === lod) registerKitPart(id, lod, toKitGeometry(m.geometry));
        }
      }
      kitPartsArrived();
    }
  });
}

/** Fetch the premium rims once (no-op without a browser or when it is already on its way). */
export function requestRimModels(): void {
  request('rims');
}

/** Fetch the period wheels (and the kit parts) once. */
export function requestWheelModels(): void {
  request('wheels');
}

/** The pack a style lives in. */
export function requestWheelStyle(style: string): void {
  if (BLENDER_RIMS.has(style)) request('rims');
  else if (BLENDER_WHEELS.has(style)) request('wheels');
}

/** Tests: forget everything loaded (the next request fetches again). */
export function resetRimModels(): void {
  ready.clear();
  requested.clear();
}

/**
 * A packed wheel (position, normal, color, `_surf`; indexed; axle +X) as the unit wheel meshes.ts wheelGeometry
 * makes: non-indexed, with the Kit's attribute set (position, normal, uv, color, surf), the game's tyre merged in
 * for a premium rim (`withTyre`; a period wheel has its own).
 */
export function assembleRim(rim: THREE.BufferGeometry, lod: RimLod, withTyre = true): THREE.BufferGeometry {
  const out = toKitGeometry(rim);
  if (!withTyre) {
    out.computeBoundingSphere();
    return out;
  }
  const tyre = tyreGeometry(BLENDER_RIM_RADIUS, lod !== 'high');
  const merged = mergeGeometries([tyre, out])!;
  out.dispose();
  merged.computeBoundingSphere();
  return merged;
}
